import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Share as ShareIcon,
  Favorite as HealthIcon,
  EmojiEvents as TrophyIcon,
  Label as TagIcon,
  AssignmentTurnedIn as RaceResultsIcon,
  Schedule as ActivityIcon,
  MilitaryTech as HeadCoachIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Team, TeamMember, User } from '../../types';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import TeamInvitationDialog from './TeamInvitationDialog';
import AthleteHealthMetricsEditor from './AthleteHealthMetricsEditor';
import AthletePREditor from './AthletePREditor';
import AthleteActivityEditor from './AthleteActivityEditor';
import AthleteTagManager from './AthleteTagManager';
import BulkRaceResultsEntry from './BulkRaceResultsEntry';
import TransferHeadCoachDialog from './TransferHeadCoachDialog';
import RoleManagementSection from './RoleManagementSection';
import SeasonResetDialog from './SeasonResetDialog';
import EditDisplayNameDialog from '../common/EditDisplayNameDialog';
import { getFirstName, sortByFirstName } from '../../utils/sortByFirstName';

interface TeamCardProps {
  team: Team;
  onTeamUpdated: () => void;
  onTeamDeleted: () => void;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, onTeamUpdated, onTeamDeleted }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [memberUsers, setMemberUsers] = useState<User[]>([]);
  const [coachUser, setCoachUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState(team.name);
  const [editDescription, setEditDescription] = useState(team.description || '');
  const [editLoading, setEditLoading] = useState(false);
  
  // Add member dialog state
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [availableAthletes, setAvailableAthletes] = useState<User[]>([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  
  // Invitation dialog state
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);

  // Health metrics editor state
  const [healthMetricsEditorOpen, setHealthMetricsEditorOpen] = useState(false);
  const [selectedAthleteForMetrics, setSelectedAthleteForMetrics] = useState<User | null>(null);

  // Activity editor state
  const [activityEditorOpen, setActivityEditorOpen] = useState(false);
  const [selectedAthleteForActivity, setSelectedAthleteForActivity] = useState<User | null>(null);

  // PR editor state
  const [prEditorOpen, setPrEditorOpen] = useState(false);
  const [selectedAthleteForPR, setSelectedAthleteForPR] = useState<User | null>(null);

  // Tag manager state
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  // Bulk race results state
  const [bulkResultsOpen, setBulkResultsOpen] = useState(false);

  // Role management state
  const [roleManagementDialogOpen, setRoleManagementDialogOpen] = useState(false);
  const [transferHeadCoachDialogOpen, setTransferHeadCoachDialogOpen] = useState(false);
  const [canTransferHeadCoach, setCanTransferHeadCoach] = useState(false);
  const [canAssignRoles, setCanAssignRoles] = useState(false);
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [canResetSeason, setCanResetSeason] = useState(false);
  
  // Season reset state
  const [seasonResetDialogOpen, setSeasonResetDialogOpen] = useState(false);

  // Edit member name state
  const [editNameMember, setEditNameMember] = useState<{ userId: string; displayName: string } | null>(null);

  const loadTeamMembers = useCallback(async () => {
    console.log('TeamCard: Loading members for team:', team.id);
    console.log('TeamCard: Current user:', user);
    console.log('TeamCard: Current user ID:', user?.id);
    // Support both headCoachId (new) and coachId (legacy) for backward compatibility
    const coachId = (team as any).headCoachId || (team as any).coachId;
    console.log('TeamCard: Team coachId/headCoachId:', coachId);
    setLoading(true);
    setError(''); // Clear any previous errors
    try {
      // Get coach details
      const coach = await userService.getUserById(coachId);
      console.log('TeamCard: Loaded coach:', coach);
      setCoachUser(coach);
      
      // Check permissions for role management
      if (user) {
        const canTransfer = await teamService.canTransferHeadCoach(team.id, user.id);
        const canAssign = await teamService.canAssignRoles(team.id, user.id);
        const canManage = await teamService.canManageTeam(team.id, user.id);
        
        // Check if user can reset season - only coaches/head coaches, not athletes
        const roleInfo = await teamService.getTeamMemberRole(team.id, user.id);
        const isCoachOrHeadCoach = 
          user.role === 'coach' || 
          user.role === 'headCoach' || 
          roleInfo?.role === 'coach' || 
          roleInfo?.role === 'headCoach';
        const canReset = isCoachOrHeadCoach && canManage;
        
        console.log('TeamCard: Permission check results:', {
          userId: user.id,
          teamId: team.id,
          roleInfo,
          userRole: user.role,
          canTransfer,
          canAssign,
          canManage,
          canResetSeason: canReset,
        });
        
        setCanTransferHeadCoach(canTransfer);
        setCanAssignRoles(canAssign);
        setCanManageTeam(canManage);
        setCanResetSeason(canReset);
      }
      
      // Get team members
      const teamMembers = await teamService.getTeamMembers(team.id);
      console.log('TeamCard: Loaded team members:', teamMembers);
      setMembers(teamMembers);
      
      // Get user details for each member
      const memberUserPromises = teamMembers.map(member => 
        userService.getUserById(member.athleteId)
      );
      const loadedMemberUsers = await Promise.all(memberUserPromises);
      console.log('TeamCard: Loaded member users:', loadedMemberUsers);
      // Keep members and memberUsers in sync, sorted by first name (alpha)
      const pairs = teamMembers
        .map((member, i) => ({ member, user: loadedMemberUsers[i] }))
        .filter((p): p is { member: TeamMember; user: User } => p.user != null);
      pairs.sort((a, b) =>
        getFirstName(a.user.displayName).localeCompare(getFirstName(b.user.displayName), undefined, { sensitivity: 'base' })
      );
      setMembers(pairs.map(p => p.member));
      setMemberUsers(pairs.map(p => p.user));
      console.log('TeamCard: Successfully loaded all data, clearing any previous errors');
    } catch (err: any) {
      console.error('TeamCard: Error loading team members:', err);
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [team.id, (team as any).headCoachId || (team as any).coachId, user]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditName(team.name);
    setEditDescription(team.description || '');
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (window.confirm(`Are you sure you want to delete the team "${team.name}"? This action cannot be undone.`)) {
      handleDeleteTeam();
    }
    handleMenuClose();
  };

  const handleManageRolesClick = () => {
    setRoleManagementDialogOpen(true);
    handleMenuClose();
  };

  const handleTransferHeadCoachClick = () => {
    setTransferHeadCoachDialogOpen(true);
    handleMenuClose();
  };

  const handleRoleUpdated = () => {
    loadTeamMembers();
    onTeamUpdated();
  };

  const handleDeleteTeam = async () => {
    setLoading(true);
    try {
      await teamService.deleteTeam(team.id);
      onTeamDeleted();
    } catch (err: any) {
      setError(err.message || 'Failed to delete team');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const updateData: any = {
        name: editName.trim(),
      };
      
      // Only include description if it's not empty
      if (editDescription.trim()) {
        updateData.description = editDescription.trim();
      }
      
      await teamService.updateTeam(team.id, updateData);
      onTeamUpdated();
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update team');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddMemberClick = async () => {
    setAddMemberDialogOpen(true);
    try {
      const athletes = await userService.getAvailableAthletes(team.id);
      setAvailableAthletes(sortByFirstName(athletes, a => a.displayName));
    } catch (err: any) {
      setError(err.message || 'Failed to load available athletes');
    }
  };

  const handleAddMemberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAthleteId) return;

    setAddMemberLoading(true);
    try {
      await teamService.addTeamMember(team.id, selectedAthleteId);
      loadTeamMembers();
      setAddMemberDialogOpen(false);
      setSelectedAthleteId('');
    } catch (err: any) {
      setError(err.message || 'Failed to add team member');
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (window.confirm('Are you sure you want to remove this member from the team?')) {
      try {
        await teamService.removeTeamMember(memberId);
        loadTeamMembers();
      } catch (err: any) {
        setError(err.message || 'Failed to remove team member');
      }
    }
  };

  const handleInviteClick = () => {
    setInvitationDialogOpen(true);
  };

  const handleCodeRegenerated = (newCode: string) => {
    // Update the team object with the new code
    const updatedTeam = { ...team, invitationCode: newCode };
    onTeamUpdated();
  };

  const handleViewProfile = (userId: string) => {
    console.log('TeamCard: handleViewProfile called with userId:', userId);
    console.log('TeamCard: Navigating to:', `/profile/${userId}`);
    navigate(`/profile/${userId}`);
  };

  const handleOpenHealthMetrics = (memberUser: User, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedAthleteForMetrics(memberUser);
    setHealthMetricsEditorOpen(true);
  };

  const handleCloseHealthMetrics = () => {
    setHealthMetricsEditorOpen(false);
    setSelectedAthleteForMetrics(null);
  };

  const handleOpenPREditor = (memberUser: User, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedAthleteForPR(memberUser);
    setPrEditorOpen(true);
  };

  const handleClosePREditor = () => {
    setPrEditorOpen(false);
    setSelectedAthleteForPR(null);
  };

  const handleOpenActivityEditor = (memberUser: User, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedAthleteForActivity(memberUser);
    setActivityEditorOpen(true);
  };

  const handleCloseActivityEditor = () => {
    setActivityEditorOpen(false);
    setSelectedAthleteForActivity(null);
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 2,
            gap: 1
          }}>
            <Typography 
              variant="h6" 
              component="h3"
              sx={{ 
                flexGrow: 1,
                wordBreak: 'break-word',
                pr: 1
              }}
            >
              {team.name}
            </Typography>
            {canManageTeam && (
              <IconButton onClick={handleMenuOpen} size="small" sx={{ flexShrink: 0 }}>
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
          
          {team.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {team.description}
            </Typography>
          )}
          
          {/* Coach Section: Head Coach + other coaches (members with role coach) */}
          {(coachUser || (members.length > 0 && memberUsers.length > 0)) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Coach{members.some(m => m.role === 'coach') ? 'es' : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {coachUser && (
                  <Chip
                    label={coachUser.displayName + (members.some(m => m.role === 'coach') ? ' (Head Coach)' : '')}
                    color="primary"
                    size="small"
                    clickable
                    onClick={() => handleViewProfile(coachUser.id)}
                    sx={{ 
                      cursor: 'pointer',
                      fontWeight: 'medium',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }}
                  />
                )}
                {members.map((member, index) => {
                  const isHeadCoach = member.athleteId === ((team as any).headCoachId || (team as any).coachId);
                  if (member.role === 'coach' && !isHeadCoach && memberUsers[index]) {
                    return (
                      <Chip
                        key={member.id}
                        label={memberUsers[index].displayName + ' (Coach)'}
                        color="primary"
                        variant="outlined"
                        size="small"
                        clickable
                        onClick={() => handleViewProfile(memberUsers[index].id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          }
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PeopleIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {members.length} member{members.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {memberUsers.map((memberUser, index) => (
                <Box key={memberUser.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    label={memberUser.displayName}
                    size="small"
                    clickable
                    onClick={() => handleViewProfile(memberUser.id)}
                    onDelete={canManageTeam ? () => handleRemoveMember(members[index].id) : undefined}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      }
                    }}
                  />
                  {canManageTeam && (
                    <>
                      <Tooltip title="Edit Name">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditNameMember({ userId: memberUser.id, displayName: memberUser.displayName });
                          }}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              color: 'primary.main',
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Adjust Health Metrics">
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenHealthMetrics(memberUser, e)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              color: 'error.main',
                            }
                          }}
                        >
                          <HealthIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Activities">
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenActivityEditor(memberUser, e)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              color: 'info.main',
                            }
                          }}
                        >
                          <ActivityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage PRs">
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenPREditor(memberUser, e)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              color: 'warning.main',
                            }
                          }}
                        >
                          <TrophyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
        
        {canManageTeam && (
          <CardActions sx={{ gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<RaceResultsIcon />}
              onClick={() => setBulkResultsOpen(true)}
              disabled={loading}
              size="small"
              variant="contained"
              color="primary"
            >
              Enter Race Results
            </Button>
            <Button
              startIcon={<TagIcon />}
              onClick={() => setTagManagerOpen(true)}
              disabled={loading}
              size="small"
              variant="outlined"
              color="secondary"
            >
              Manage Tags
            </Button>
            {canResetSeason && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => setSeasonResetDialogOpen(true)}
                disabled={loading}
                size="small"
                variant="outlined"
                color="warning"
              >
                Reset Season
              </Button>
            )}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddMemberClick}
              disabled={loading}
              size="small"
            >
              Add Member
            </Button>
            <Button
              startIcon={<ShareIcon />}
              onClick={handleInviteClick}
              disabled={loading}
              size="small"
              variant="outlined"
            >
              Invite
            </Button>
          </CardActions>
        )}
      </Card>

      {/* Edit Team Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleEditSubmit}>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Team Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              margin="normal"
              required
              disabled={editLoading}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={editLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={editLoading || !editName.trim()}>
              {editLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleAddMemberSubmit}>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Manual Addition:</strong> This will add an athlete directly to your team. 
              Normally, athletes should join using the invitation code. Use this feature only for administrative purposes.
            </Alert>
            <TextField
              fullWidth
              select
              label="Select Athlete"
              value={selectedAthleteId}
              onChange={(e) => setSelectedAthleteId(e.target.value)}
              margin="normal"
              required
              disabled={addMemberLoading}
            >
              {availableAthletes.map((athlete) => (
                <MenuItem key={athlete.id} value={athlete.id}>
                  {athlete.displayName} ({athlete.email})
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddMemberDialogOpen(false)} disabled={addMemberLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={addMemberLoading || !selectedAthleteId}
            >
              {addMemberLoading ? <CircularProgress size={20} /> : 'Add Member'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Team</ListItemText>
        </MenuItem>
        {canTransferHeadCoach && (
          <MenuItem onClick={handleTransferHeadCoachClick}>
            <ListItemIcon>
              <HeadCoachIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Transfer Head Coach</ListItemText>
          </MenuItem>
        )}
        {canAssignRoles && (
          <MenuItem onClick={handleManageRolesClick}>
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Roles</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Team</ListItemText>
        </MenuItem>
      </Menu>

      {/* Team Invitation Dialog */}
      <TeamInvitationDialog
        open={invitationDialogOpen}
        onClose={() => setInvitationDialogOpen(false)}
        team={team}
        onCodeRegenerated={handleCodeRegenerated}
      />

      {/* Health Metrics Editor Dialog */}
      {selectedAthleteForMetrics && (
        <AthleteHealthMetricsEditor
          open={healthMetricsEditorOpen}
          onClose={handleCloseHealthMetrics}
          athlete={selectedAthleteForMetrics}
          teamId={team.id}
        />
      )}

      {/* Activity Editor Dialog */}
      {selectedAthleteForActivity && (
        <AthleteActivityEditor
          open={activityEditorOpen}
          onClose={handleCloseActivityEditor}
          athlete={selectedAthleteForActivity}
          teamId={team.id}
        />
      )}

      {/* PR Editor Dialog */}
      {selectedAthleteForPR && (
        <AthletePREditor
          open={prEditorOpen}
          onClose={handleClosePREditor}
          athlete={selectedAthleteForPR}
          teamId={team.id}
        />
      )}

      {/* Athlete Tag Manager Dialog */}
      <AthleteTagManager
        open={tagManagerOpen}
        onClose={() => setTagManagerOpen(false)}
        teamId={team.id}
        onTagsUpdated={loadTeamMembers}
      />

      {/* Bulk Race Results Entry Dialog */}
      <BulkRaceResultsEntry
        open={bulkResultsOpen}
        onClose={() => setBulkResultsOpen(false)}
        teamId={team.id}
      />

      {/* Role Management Dialog */}
      <Dialog
        open={roleManagementDialogOpen}
        onClose={() => setRoleManagementDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Team Roles</DialogTitle>
        <DialogContent>
          <RoleManagementSection
            teamId={team.id}
            onRoleUpdated={handleRoleUpdated}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleManagementDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Head Coach Dialog */}
      <TransferHeadCoachDialog
        open={transferHeadCoachDialogOpen}
        onClose={() => setTransferHeadCoachDialogOpen(false)}
        teamId={team.id}
        onTransferComplete={handleRoleUpdated}
      />

      {/* Season Reset Dialog */}
      <SeasonResetDialog
        open={seasonResetDialogOpen}
        onClose={() => setSeasonResetDialogOpen(false)}
        teamId={team.id}
        teamMembers={members}
        onResetComplete={() => {
          loadTeamMembers();
          onTeamUpdated();
        }}
      />

      {/* Edit Team Member Name Dialog */}
      {editNameMember && user && (
        <EditDisplayNameDialog
          open={!!editNameMember}
          onClose={() => setEditNameMember(null)}
          currentDisplayName={editNameMember.displayName}
          onSave={async (newDisplayName) => {
            await userService.updateDisplayName(editNameMember.userId, newDisplayName, {
              teamId: team.id,
              currentUserId: user.id,
            });
            await loadTeamMembers();
          }}
          title="Edit Team Member Name"
          label="Display name"
        />
      )}
    </>
  );
};

export default TeamCard;
